import { useFrame } from '@react-three/fiber'
import { RefObject, useEffect, useRef } from 'react'
import {
    BufferAttribute, BufferGeometry,
    Group,
    NormalBlending, Points, PointsMaterial, Vector3,
} from 'three'
import {
    type FootstepStepEvent,
    type FootstepTimelineRef,
    footprintSettings,
} from './footstepConfig'
import { useFootstepEffectTextures } from './useFootstepEffectTextures'
import { store } from '@/src/stores/store'

const leavesSettings = {
    color: '#ffffff',
    previewCount: 4,
    gameplayCount: 8,
    size: 0.5,
    sizeJitter: 0.18,
    opacity: 0.9,
    spawnWidth: 0.14,
    spawnDepth: 0.12,
    spawnHeightMin: 0.03,
    spawnHeightMax: 0.16,
    spawnBackOffset: -0.1,
    spawnCentering: 1,
    emitterHeightOffset: -0.5,
    spiralRadiusMin: 1,
    spiralRadiusMax: 1.28,
    spiralAngularSpeedMin: 4.8,
    spiralAngularSpeedMax: 7.8,
    spiralBackwardInfluence: 0.55,
    backwardTravelMin: 2,
    backwardTravelMax: 3,
    upwardArcHeightMin: 2,
    upwardArcHeightMax: 3,
    gameplayUpwardArcHeightMin: 4,
    gameplayUpwardArcHeightMax: 5,
    liftDelayMin: 0.08,
    liftDelayMax: 0.18,
    fallStrengthMin: 0.5,
    fallStrengthMax: 0.9,
    outwardBiasMin: 0.2,
    outwardBiasMax: 1,
    lifetime: 1,
    previewMatchStepInterval: 0.1,
    hueOffsetRange: 0.02,
    saturationOffsetRange: 0.1,
    rotationSpeed: 1.0,
    fadeStartNormalized: 0.75,
    gameplaySizeMultiplier: 0.35,
}

type LeavesBurst = {
    age: number
    backwardTravels: Float32Array
    baseBackwardOffsets: Float32Array
    baseHeightOffsets: Float32Array
    baseLateralOffsets: Float32Array
    fallStrengths: Float32Array
    geometry: BufferGeometry
    parent: Group
    liftDelays: Float32Array
    material: PointsMaterial
    outwardBiases: Float32Array
    points: Points
    positions: Float32Array
    right: Vector3
    rotationUniforms: { uTime: { value: number } }
    spiralAngularSpeeds: Float32Array
    spiralAngles: Float32Array
    spiralRadii: Float32Array
    up: Vector3
    upwardArcHeights: Float32Array
    forward: Vector3
}

function createLeavesMaterial(texture: any, size: number): PointsMaterial {
    const material = new PointsMaterial({
        color: leavesSettings.color,
        size,
        map: texture,
        transparent: true,
        opacity: leavesSettings.opacity,
        depthWrite: false,
        blending: NormalBlending,
        sizeAttenuation: true,
        toneMapped: false,
    })
    material.userData.rotationUniforms = { uTime: { value: 0 } }
    material.onBeforeCompile = (shader) => {
        shader.uniforms.uTime = material.userData.rotationUniforms.uTime
        shader.vertexShader = shader.vertexShader
            .replace('#include <common>', `#include <common>
attribute float aHueOffset;
attribute float aRotation;
attribute float aSizeScale;
attribute float aSaturationOffset;
attribute float aSpinSpeed;
uniform float uTime;
varying float vHueOffset;
varying float vParticleRotation;
varying float vSaturationOffset;`)
            .replace('#include <begin_vertex>', `#include <begin_vertex>
vHueOffset = aHueOffset;
vParticleRotation = aRotation + (uTime * aSpinSpeed);
vSaturationOffset = aSaturationOffset;`)
            .replace('#include <project_vertex>', `#include <project_vertex>
gl_PointSize *= aSizeScale;`)
        shader.fragmentShader = shader.fragmentShader
            .replace('#include <common>', `#include <common>
varying float vHueOffset;
varying float vParticleRotation;
varying float vSaturationOffset;
vec3 rgbToHsv(vec3 c){vec4 K=vec4(0.,-1./3.,2./3.,-1.);vec4 p=mix(vec4(c.bg,K.wz),vec4(c.gb,K.xy),step(c.b,c.g));vec4 q=mix(vec4(p.xyw,c.r),vec4(c.r,p.yzx),step(p.x,c.r));float d=q.x-min(q.w,q.y);float e=1e-10;return vec3(abs(q.z+(q.w-q.y)/(6.*d+e)),d/(q.x+e),q.x);}
vec3 hsvToRgb(vec3 c){vec3 rgb=clamp(abs(mod(c.x*6.+vec3(0,4,2),6.)-3.)-1.,0.,1.);rgb=rgb*rgb*(3.-2.*rgb);return c.z*mix(vec3(1.),rgb,c.y);}
vec2 getRotatedUV(){vec2 c=gl_PointCoord-.5;float s=sin(vParticleRotation),cs=cos(vParticleRotation);return vec2(c.x*cs-c.y*s,c.x*s+c.y*cs)+.5;}`)
            .replace('#include <map_particle_fragment>', `#if defined(USE_MAP)||defined(USE_ALPHAMAP)
vec2 rotUV=getRotatedUV();vec2 uv=(uvTransform*vec3(rotUV.x,1.-rotUV.y,1)).xy;
#endif
#ifdef USE_MAP
vec4 mt=texture2D(map,uv);vec3 hsv=rgbToHsv(clamp(mt.rgb,0.,1.));hsv.x=fract(hsv.x+vHueOffset);hsv.y=clamp(hsv.y+vSaturationOffset,0.,1.);diffuseColor.rgb*=hsvToRgb(hsv);diffuseColor.a*=mt.a;
#endif
#ifdef USE_ALPHAMAP
diffuseColor.a*=texture2D(alphaMap,uv).g;
#endif`)
    }
    material.customProgramCacheKey = () => 'footstep-leaves-v3'
    return material
}

type EffectRunLeavesProps = {
    anchorRef: RefObject<Group | null>
    timelineRef: FootstepTimelineRef
}

export function EffectRunLeaves({ anchorRef, timelineRef }: EffectRunLeavesProps) {

    const activeBurstsRef = useRef<LeavesBurst[]>([])
    const lastSpawnTimestampRef = useRef<number | null>(null)
    const isPreviewMode =
        store.appFlowPhase === 'configurator' &&
        store.configuratorActiveTab === 'effect' &&
        store.footstepEffect === 'leaves'

    const { leavesTexture } = useFootstepEffectTextures()

    useEffect(() => {
        return () => {
            for (const b of activeBurstsRef.current) {
                b.parent.remove(b.points); b.geometry.dispose(); b.material.dispose()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function spawnBurst(step: FootstepStepEvent) {
        const anchor = anchorRef.current
        if (!anchor) return

        const footX = step.side === 'left' ? footprintSettings.leftFootX : footprintSettings.rightFootX
        const spawnPosition = new Vector3(
            footX * (1 - leavesSettings.spawnCentering),
            leavesSettings.emitterHeightOffset,
            leavesSettings.spawnBackOffset,
        )
        const count = Math.max(1, Math.floor(
            isPreviewMode ? leavesSettings.previewCount : leavesSettings.gameplayCount,
        ))
        const positions = new Float32Array(count * 3)
        const hueOffsets = new Float32Array(count)
        const rotations = new Float32Array(count)
        const sizeScales = new Float32Array(count)
        const saturationOffsets = new Float32Array(count)
        const spinSpeeds = new Float32Array(count)
        const baseLateralOffsets = new Float32Array(count)
        const baseBackwardOffsets = new Float32Array(count)
        const baseHeightOffsets = new Float32Array(count)
        const spiralRadii = new Float32Array(count)
        const spiralAngles = new Float32Array(count)
        const spiralAngularSpeeds = new Float32Array(count)
        const backwardTravels = new Float32Array(count)
        const upwardArcHeights = new Float32Array(count)
        const liftDelays = new Float32Array(count)
        const fallStrengths = new Float32Array(count)
        const outwardBiases = new Float32Array(count)

        const geometry = new BufferGeometry()
        geometry.setAttribute('position', new BufferAttribute(positions, 3))
        geometry.setAttribute('aHueOffset', new BufferAttribute(hueOffsets, 1))
        geometry.setAttribute('aRotation', new BufferAttribute(rotations, 1))
        geometry.setAttribute('aSizeScale', new BufferAttribute(sizeScales, 1))
        geometry.setAttribute('aSaturationOffset', new BufferAttribute(saturationOffsets, 1))
        geometry.setAttribute('aSpinSpeed', new BufferAttribute(spinSpeeds, 1))

        for (let i = 0; i < count; i++) {
            rotations[i] = Math.random() * Math.PI * 2
            spinSpeeds[i] = (Math.random() * 2 - 1) * leavesSettings.rotationSpeed
            hueOffsets[i] = (Math.random() * 2 - 1) * leavesSettings.hueOffsetRange
            saturationOffsets[i] = (Math.random() * 2 - 1) * leavesSettings.saturationOffsetRange
            sizeScales[i] = 1 + (Math.random() * 2 - 1) * leavesSettings.sizeJitter
        }

        for (let i = 0; i < count; i++) {
            const i3 = i * 3
            const lateralOffset = (Math.random() * 2 - 1) * leavesSettings.spawnWidth
            const backwardOffset = Math.random() * leavesSettings.spawnDepth
            const heightOffset =
                leavesSettings.spawnHeightMin
                + Math.random() * (leavesSettings.spawnHeightMax - leavesSettings.spawnHeightMin)
            const swirlDirection = Math.random() > 0.5 ? 1 : -1
            const spiralRadius =
                leavesSettings.spiralRadiusMin
                + Math.random() * (leavesSettings.spiralRadiusMax - leavesSettings.spiralRadiusMin)
            const spiralAngularSpeed =
                leavesSettings.spiralAngularSpeedMin
                + Math.random() * (leavesSettings.spiralAngularSpeedMax - leavesSettings.spiralAngularSpeedMin)
            const backwardTravel =
                leavesSettings.backwardTravelMin
                + Math.random() * (leavesSettings.backwardTravelMax - leavesSettings.backwardTravelMin)
            const upwardArcHeightMin = isPreviewMode
                ? leavesSettings.upwardArcHeightMin
                : leavesSettings.gameplayUpwardArcHeightMin
            const upwardArcHeightMax = isPreviewMode
                ? leavesSettings.upwardArcHeightMax
                : leavesSettings.gameplayUpwardArcHeightMax
            const upwardArcHeight =
                upwardArcHeightMin
                + Math.random() * (upwardArcHeightMax - upwardArcHeightMin)
            const liftDelay =
                leavesSettings.liftDelayMin
                + Math.random() * (leavesSettings.liftDelayMax - leavesSettings.liftDelayMin)
            const fallStrength =
                leavesSettings.fallStrengthMin
                + Math.random() * (leavesSettings.fallStrengthMax - leavesSettings.fallStrengthMin)
            const outwardBias =
                leavesSettings.outwardBiasMin
                + Math.random() * (leavesSettings.outwardBiasMax - leavesSettings.outwardBiasMin)

            baseLateralOffsets[i] = lateralOffset
            baseBackwardOffsets[i] = backwardOffset
            baseHeightOffsets[i] = heightOffset
            spiralRadii[i] = spiralRadius
            spiralAngles[i] = Math.random() * Math.PI * 0.45 - Math.PI * 0.225
            spiralAngularSpeeds[i] = spiralAngularSpeed * swirlDirection
            backwardTravels[i] = backwardTravel
            upwardArcHeights[i] = upwardArcHeight
            liftDelays[i] = liftDelay
            fallStrengths[i] = fallStrength
            outwardBiases[i] = outwardBias * (lateralOffset >= 0 ? 1 : -1)

            const localX = lateralOffset
            const localY = heightOffset
            const localZ = backwardOffset
            const rotatedLocalX = -localX
            const rotatedLocalY = localZ
            const rotatedLocalZ = localY

            positions[i3] = rotatedLocalX
            positions[i3 + 1] = rotatedLocalY
            positions[i3 + 2] = rotatedLocalZ
        }

        const size = isPreviewMode
            ? leavesSettings.size
            : leavesSettings.size * leavesSettings.gameplaySizeMultiplier
        const material = createLeavesMaterial(leavesTexture, size)
        const rotationUniforms = material.userData.rotationUniforms as { uTime: { value: number } }

        const points = new Points(geometry, material)
        points.position.copy(spawnPosition)
        anchor.add(points)
        activeBurstsRef.current.push({
            age: 0,
            backwardTravels,
            baseBackwardOffsets,
            baseHeightOffsets,
            baseLateralOffsets,
            fallStrengths,
            geometry,
            parent: anchor,
            liftDelays,
            material,
            outwardBiases,
            points,
            positions,
            right: new Vector3(1, 0, 0),
            rotationUniforms,
            spiralAngularSpeeds,
            spiralAngles,
            spiralRadii,
            up: new Vector3(0, 1, 0),
            upwardArcHeights,
            forward: new Vector3(0, 0, -1),
        })
    }

    useFrame((_, delta) => {
        const safeDelta = Math.min(delta, 1 / 60)
        const timeline = timelineRef.current

        for (let i = activeBurstsRef.current.length - 1; i >= 0; i--) {
            const b = activeBurstsRef.current[i]
            b.age += safeDelta
            const t = b.age / leavesSettings.lifetime
            if (t >= 1) {
                b.parent.remove(b.points)
                b.geometry.dispose()
                b.material.dispose()
                activeBurstsRef.current.splice(i, 1)
                continue
            }

            const floorLocalY = -b.points.position.y
            for (let p = 0; p < b.positions.length; p += 3) {
                const particleIndex = p / 3
                const liftDelay = b.liftDelays[particleIndex]
                const delayedT = Math.max(0, (t - liftDelay) / Math.max(1 - liftDelay, 0.001))
                const liftT = delayedT * delayedT * (3 - 2 * delayedT)
                const angle = b.spiralAngles[particleIndex] + b.spiralAngularSpeeds[particleIndex] * b.age
                const spiralRadius = b.spiralRadii[particleIndex] * (0.35 + 0.85 * liftT)
                const sideSpiral = Math.sin(angle) * spiralRadius
                const backSpiral = Math.cos(angle) * spiralRadius * leavesSettings.spiralBackwardInfluence
                const outwardSway = b.outwardBiases[particleIndex] * Math.sin(Math.min(t * 1.2, 1) * Math.PI)
                const backwardTravel = b.backwardTravels[particleIndex] * (0.2 + 0.8 * liftT)
                const riseArc = Math.sin(liftT * Math.PI * 0.9)
                const height =
                    b.baseHeightOffsets[particleIndex]
                    + b.upwardArcHeights[particleIndex] * riseArc
                    - b.fallStrengths[particleIndex] * Math.max(0, t - 0.62) * Math.max(0, t - 0.62)

                const localX = b.baseLateralOffsets[particleIndex] + outwardSway + sideSpiral
                const localZ = b.baseBackwardOffsets[particleIndex] + backwardTravel + backSpiral
                const localY = Math.max(floorLocalY, height)

                const rotatedLocalX = -localX
                const rotatedLocalY = localZ
                const rotatedLocalZ = localY

                b.positions[p] =
                    b.right.x * rotatedLocalX
                    - b.forward.x * rotatedLocalZ
                    + b.up.x * rotatedLocalY
                b.positions[p + 1] =
                    b.right.y * rotatedLocalX
                    - b.forward.y * rotatedLocalZ
                    + b.up.y * rotatedLocalY
                b.positions[p + 2] =
                    b.right.z * rotatedLocalX
                    - b.forward.z * rotatedLocalZ
                    + b.up.z * rotatedLocalY
            }
            b.geometry.attributes.position.needsUpdate = true
            b.rotationUniforms.uTime.value = b.age
            const fadeProgress = Math.max(
                0,
                (t - leavesSettings.fadeStartNormalized) / (1 - leavesSettings.fadeStartNormalized),
            )
            b.material.opacity = leavesSettings.opacity * (1 - fadeProgress)
        }

        if (!timeline.footstepsEnabled) {
            lastSpawnTimestampRef.current = null
            return
        }

        for (const step of timeline.steps) {
            const lastSpawnTimestamp = lastSpawnTimestampRef.current
            if (
                lastSpawnTimestamp != null &&
                step.timestamp - lastSpawnTimestamp < leavesSettings.previewMatchStepInterval
            ) {
                continue
            }
            spawnBurst(step)
            lastSpawnTimestampRef.current = step.timestamp
        }
    })

    return null
}












